import React, { useState, useMemo, useCallback } from "react";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  Button,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import styles from "./document-analyzer.module.scss";
import MarkdownRenderer from "../../MarkdownRenderer";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import { recordAgentAction } from "../../../store/agentSlice";
import DocumentCard from "../shared/DocumentCard";
import DocumentToolbar from "../shared/DocumentToolbar";
import SourceVerificationSlider from "../shared/SourceVerificationSlider";

interface Region {
  page_number: number;
  polygon: number[];
  page_width: number;
  page_height: number;
  unit: string;
}

interface SourceLocation {
  document_url?: string;
  page_number: number;
  polygon: number[];
  coordinate_system: string;
  page_width: number;
  page_height: number;
  unit: string;
  regions?: Region[];
}

interface ClauseReference {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  source_location?: SourceLocation;
  is_verified?: boolean;
}

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface ActionItem {
  action: string;
  reason: string;
  responsible_party?: string;
  clause_reference?: string;
}

interface DocumentAnalysis {
  document_name: string;
  contract_details: ContractDetails;
  is_coc_clause_present: boolean;
  clause_reference: ClauseReference[];
  extracted_threshold: string;
  transaction_percentage: string;
  trigger_result: string;
  reason: string;
  action_required: ActionItem[] | string | string[];
  consequence_summary: string;
  consequence_severity?: string;
  risk_tier: string;
  human_review: string;
  document_url?: string;
  is_verified?: boolean;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentAnalysis[];
  };
  question?: string;
}

interface DocumentAnalyzerAgentProps {
  parsedContent: ParsedContent;
  messageId?: string;
}

interface DocumentFeedback {
  feedback_comment: string;
}

const DOCUMENT_ANALYZER_ENDPOINT = "/stream/coc-review";

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function DocumentAnalyzerAgent({
  parsedContent,
  messageId,
}: DocumentAnalyzerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const numericAgentId = currentAgent?.id || 11;
  const documents = parsedContent?.finalAnswer?.documents || [];
  const [sliderData, setSliderData] = useState<{
    documents: Array<{
      documentName: string;
      documentUrl?: string;
      clauses: ClauseReference[];
      documentIndex: number;
    }>;
    initialDocumentIndex: number;
  } | null>(null);
  const [feedbackData, setFeedbackData] = useState<
    Record<number, DocumentFeedback>
  >({});
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [sortBy, setSortBy] = useState("document-order");
  const [filterBy, setFilterBy] = useState("all");
  const [riskTierFilter, setRiskTierFilter] = useState("all");
  const [expandedDocs, setExpandedDocs] = useState<Record<number, boolean>>({});

  const isLatestMessage =
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
    messageId;

  // Sorting and filtering logic
  const sortedAndFilteredDocuments = useMemo(() => {
    let filtered = [...documents];

    // Apply verification filter
    if (filterBy !== "all") {
      filtered = filtered.filter((doc) => {
        switch (filterBy) {
          case "verified":
            return doc.is_verified === true;
          case "not-verified":
            return doc.is_verified === false || doc.is_verified === undefined;
          default:
            return true;
        }
      });
    }

    // Apply risk tier filter
    if (riskTierFilter !== "all") {
      filtered = filtered.filter((doc) => {
        const normalizedRiskTier = doc.risk_tier?.toLowerCase();
        switch (riskTierFilter) {
          case "high":
            return (
              normalizedRiskTier === "high" || normalizedRiskTier === "red"
            );
          case "medium":
            return (
              normalizedRiskTier === "medium" || normalizedRiskTier === "yellow"
            );
          case "low":
            return (
              normalizedRiskTier === "low" || normalizedRiskTier === "green"
            );
          default:
            return true;
        }
      });
    }

    // Apply sort
    const sorted = [...filtered];
    switch (sortBy) {
      case "risk-high":
        sorted.sort((a, b) => {
          const riskOrder: Record<string, number> = {
            high: 3,
            red: 3,
            medium: 2,
            yellow: 2,
            low: 1,
            green: 1,
          };
          const aRisk = riskOrder[a.risk_tier?.toLowerCase() || ""] || 0;
          const bRisk = riskOrder[b.risk_tier?.toLowerCase() || ""] || 0;
          return bRisk - aRisk;
        });
        break;
      case "risk-low":
        sorted.sort((a, b) => {
          const riskOrder: Record<string, number> = {
            high: 3,
            red: 3,
            medium: 2,
            yellow: 2,
            low: 1,
            green: 1,
          };
          const aRisk = riskOrder[a.risk_tier?.toLowerCase() || ""] || 0;
          const bRisk = riskOrder[b.risk_tier?.toLowerCase() || ""] || 0;
          return aRisk - bRisk;
        });
        break;
      case "name-asc":
        sorted.sort((a, b) => a.document_name.localeCompare(b.document_name));
        break;
      case "name-desc":
        sorted.sort((a, b) => b.document_name.localeCompare(a.document_name));
        break;
      default:
        break;
    }

    return sorted;
  }, [documents, sortBy, filterBy, riskTierFilter]);

  const handleClauseClick = (clickedDocIndex: number) => {
    // Prepare all documents with clauses for the slider
    // Note: source_location is at CLAUSE level only
    const allDocuments = sortedAndFilteredDocuments.map((doc, index) => ({
      documentName: doc.document_name,
      documentUrl: doc.document_url,
      clauses: doc.clause_reference || [], // Each clause has its own source_location
      documentIndex: index,
    }));

    // Find the clicked document in the full documents array
    const clickedDoc = documents[clickedDocIndex];
    const sliderDocIndex = allDocuments.findIndex(
      (d) => d.documentName === clickedDoc.document_name,
    );

    setSliderData({
      documents: allDocuments,
      initialDocumentIndex: sliderDocIndex >= 0 ? sliderDocIndex : 0,
    });
  };

  const handleCloseSlider = () => {
    setSliderData(null);
  };

  const handleResetFilters = useCallback(() => {
    setFilterBy("all");
    setRiskTierFilter("all");
  }, []);

  const handleFeedbackChange = (docIndex: number, value: string) => {
    setFeedbackData((prev) => ({
      ...prev,
      [docIndex]: {
        feedback_comment: value,
      },
    }));
  };

  const isFeedbackComplete = () => {
    // Feedback is optional - always returns true since we only have comment field
    return true;
  };

  const handleConfirmation = (response: "yes" | "no") => {
    if (!currentAgent?.id) return;

    // Prepare feedback array with comments
    const documentFeedback = documents
      .map((doc, index) => {
        const feedback = feedbackData[index];
        if (feedback && feedback.feedback_comment.trim()) {
          return {
            document_name: doc.document_name,
            feedback_comment: feedback.feedback_comment,
          };
        }
        return null;
      })
      .filter(Boolean);

    // Create message with response and feedback data
    const messagePayload = {
      response:
        response === "yes"
          ? "Yes, I am satisfied with this outcome."
          : "No, I would like to adjust something.",
      document_feedback: documentFeedback,
    };

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: JSON.stringify(messagePayload),
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: DOCUMENT_ANALYZER_ENDPOINT,
        sessionId: null,
      }),
    );

    setFeedbackSubmitted(true);
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No documents found for analysis.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>Change of Control Analysis</h2>
      </div>

      <DocumentToolbar
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterBy={filterBy}
        onFilterChange={setFilterBy}
        riskTierFilter={riskTierFilter}
        onRiskTierFilterChange={setRiskTierFilter}
        onResetFilters={handleResetFilters}
        instructionText="Each document has its own card below. Check the source and mark each finding Verified or Not Verified, then finalize."
      />

      <div className={styles.documentsWrapper}>
        {sortedAndFilteredDocuments.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              color: "#666",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: "#999" }}>
              No documents match your filters
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Try adjusting your verification or risk tier filters to see more
              results.
            </Typography>
          </Box>
        ) : (
          sortedAndFilteredDocuments.map((doc, index) => (
            <DocumentCard
              key={index}
              documentName={doc.document_name}
              riskTier={doc.risk_tier}
              isExpanded={expandedDocs[index] !== false}
              onToggleExpand={() =>
                setExpandedDocs((prev) => ({ ...prev, [index]: !prev[index] }))
              }
            >
              <div className={styles.documentDetailsTable}>
                <table className={styles.detailsTable}>
                  <thead>
                    <tr>
                      <th>CONTRACT DETAILS</th>
                      <th>IDENTIFY COC</th>
                      <th>CLAUSE REFERENCE</th>
                      <th>IDENTIFY COC TRIGGER</th>
                      <th>ACTION ITEMS</th>
                      <th>CONSEQUENCE OF NOT ADHERING TO COC REQUIREMENTS</th>
                      <th>RISK TIER</th>
                      {/* <th>REVIEW</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.contractDetailsCell}>
                        {doc.contract_details ? (
                          <div className={styles.contractInfo}>
                            <div>
                              <strong>Type:</strong>{" "}
                              <span>{doc.contract_details.contract_type}</span>
                            </div>
                            <div>
                              <strong>Parties:</strong>{" "}
                              <span>
                                {formatContractingParties(
                                  doc.contract_details.contracting_parties,
                                )}
                              </span>
                            </div>
                            <div>
                              <strong>Date:</strong>{" "}
                              <span>{doc.contract_details.agreement_date}</span>
                            </div>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className={styles.centered}>
                        <span
                          className={
                            doc.is_coc_clause_present ? styles.yes : styles.no
                          }
                        >
                          {doc.is_coc_clause_present ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className={styles.clauseCell}>
                        {doc.clause_reference &&
                        doc.clause_reference.length > 0 ? (
                          <div className={styles.clauseWithButton}>
                            <span>
                              {doc.clause_reference
                                .map((ref) => ref.clause_number)
                                .filter(Boolean)
                                .join(", ") || "View"}
                            </span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleClauseClick(index)}
                              className={styles.verifySourceButton}
                            >
                              Verify source
                            </Button>
                          </div>
                        ) : (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Could not verify this clause reference
                          </span>
                        )}
                      </td>
                      <td className={styles.centered}>
                        <Tooltip
                          title={doc.reason || "N/A"}
                          arrow
                          placement="top"
                        >
                          <span className={styles.triggerBadge}>
                            {doc.trigger_result}
                          </span>
                        </Tooltip>
                      </td>
                      <td className={styles.actionCell}>
                        {Array.isArray(doc.action_required) ? (
                          <ul>
                            {doc.action_required.map((item, idx) => {
                              // Check if it's the new object format with action and reason
                              if (
                                typeof item === "object" &&
                                item !== null &&
                                "action" in item
                              ) {
                                return (
                                  <li key={idx}>
                                    <strong>Action:</strong> {item.action}
                                    <br />
                                    <strong>Reason:</strong> {item.reason}
                                    {item.responsible_party && (
                                      <>
                                        <br />
                                        <strong>Responsible Party:</strong>{" "}
                                        {item.responsible_party}
                                      </>
                                    )}
                                    {item.clause_reference && (
                                      <>
                                        <br />
                                        <strong>Clause Reference:</strong>{" "}
                                        {item.clause_reference}
                                      </>
                                    )}
                                  </li>
                                );
                              }
                              // Fallback for legacy string format
                              return <li key={idx}>{item}</li>;
                            })}
                          </ul>
                        ) : (
                          doc.action_required || "N/A"
                        )}
                      </td>
                      <td
                        className={styles.consequenceCell}
                        style={{ fontSize: "14px", lineHeight: 1.5 }}
                      >
                        {doc.consequence_summary || "N/A"}
                        {doc.consequence_severity && (
                          <div style={{ marginTop: "8px" }}>
                            Consequence Severity: {doc.consequence_severity}
                          </div>
                        )}
                      </td>
                      <td className={styles.centered}>
                        <span
                          className={`${styles.riskBadge} ${
                            styles[getRiskTierColor(doc.risk_tier)]
                          }`}
                        ></span>
                      </td>
                      {/* <td className={styles.reviewCell}>
                        {doc.is_verified === true ? (
                          <span className={styles.verifiedBadge}>Verified</span>
                        ) : (
                          <span className={styles.notVerifiedBadge}>
                            Not Verified
                          </span>
                        )}
                      </td> */}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* {isLatestMessage && (
                <div className={styles.feedbackSection}>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter your feedback (optional)..."
                    value={feedbackData[index]?.feedback_comment || ""}
                    onChange={(e) =>
                      handleFeedbackChange(index, e.target.value)
                    }
                    disabled={feedbackSubmitted}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "13px",
                        backgroundColor: feedbackSubmitted ? "#f5f5f5" : "#fff",
                      },
                    }}
                  />
                </div>
              )} */}
            </DocumentCard>
          ))
        )}
      </div>

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
            {isLatestMessage && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e8e8e8",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => handleConfirmation("yes")}
                  disabled={feedbackSubmitted}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#00a888",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#00947a" },
                    "&.Mui-disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  Yes, I'm satisfied
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleConfirmation("no")}
                  disabled={feedbackSubmitted}
                  sx={{
                    textTransform: "none",
                    borderColor: "#00a888",
                    color: "#00a888",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: "#00947a",
                      backgroundColor: "rgba(0, 168, 136, 0.04)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  No, I'd like to modify
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Source Verification Slider */}
      {sliderData && (
        <SourceVerificationSlider
          open={Boolean(sliderData)}
          onClose={handleCloseSlider}
          documents={sliderData.documents}
          initialDocumentIndex={sliderData.initialDocumentIndex}
          mode="readonly"
        />
      )}
    </div>
  );
}
